import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faSpinner, faEdit } from '@fortawesome/free-solid-svg-icons';
import { Site, FortnoxCustomerDebtAccount as FortnoxCustomerDebtAccountType } from '@/types';
import { apiService } from '@/utils/api';

interface FortnoxCustomerDebtAccountsProps {
  className?: string;
}

const FortnoxCustomerDebtAccounts: React.FC<FortnoxCustomerDebtAccountsProps> = ({ className }) => {
  const queryClient = useQueryClient();
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<FortnoxCustomerDebtAccountType | null>(null);
  const [formData, setFormData] = useState<Partial<FortnoxCustomerDebtAccountType>>({});
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

  // Fetch sites
  const { data: sites = [], isLoading: sitesLoading } = useQuery(
    ['sites'],
    () => apiService.getSites()
  );

  // Set default site when sites are loaded
  useEffect(() => {
    if (sites.length > 0 && !selectedSiteId) {
      setSelectedSiteId(sites[0].id);
    }
  }, [sites, selectedSiteId]);

  // Fetch Fortnox customer debt accounts
  const { data: customerDebtAccounts = [], isLoading: accountsLoading } = useQuery(
    ['fortnox-customer-debt-accounts', selectedSiteId],
    () => apiService.getFortnoxCustomerDebtAccounts(selectedSiteId!),
    {
      enabled: !!selectedSiteId,
    }
  );

  // Save mutation
  const saveMutation = useMutation(
    async (data: FortnoxCustomerDebtAccountType) => {
      if (editingAccount?.id) {
        return apiService.updateFortnoxCustomerDebtAccount(selectedSiteId!, editingAccount.id, data);
      } else {
        return apiService.createFortnoxCustomerDebtAccount(selectedSiteId!, data);
      }
    },
    {
      onSuccess: () => {
        setMessage({ type: 'success', text: 'Customer debt account saved successfully' });
        queryClient.invalidateQueries(['fortnox-customer-debt-accounts']);
        handleCloseModal();
        setTimeout(() => setMessage(null), 3000);
      },
      onError: () => {
        setMessage({ type: 'error', text: 'Something went wrong' });
        setTimeout(() => setMessage(null), 3000);
      },
    }
  );

  // Delete mutation
  const deleteMutation = useMutation(
    async (accountId: number) => {
      return apiService.deleteFortnoxCustomerDebtAccount(selectedSiteId!, accountId);
    },
    {
      onSuccess: () => {
        setMessage({ type: 'success', text: 'Customer debt account deleted successfully' });
        queryClient.invalidateQueries(['fortnox-customer-debt-accounts']);
        setTimeout(() => setMessage(null), 3000);
      },
      onError: () => {
        setMessage({ type: 'error', text: 'Something went wrong' });
        setTimeout(() => setMessage(null), 3000);
      },
    }
  );

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSiteChange = (siteId: number) => {
    setSelectedSiteId(siteId);
  };

  const handleOpenModal = () => {
    setEditingAccount(null);
    setFormData({});
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingAccount(null);
    setFormData({});
  };

  const handleEditAccount = (account: FortnoxCustomerDebtAccountType) => {
    setEditingAccount(account);
    setFormData(account);
    setIsModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSiteId) return;
    
    const submitData: FortnoxCustomerDebtAccountType = {
      ...formData,
      site_id: selectedSiteId,
      account: formData.account ? parseInt(formData.account.toString()) : 0,
      payment_account: formData.payment_account ? parseInt(formData.payment_account.toString()) : 0,
    } as FortnoxCustomerDebtAccountType;
    
    saveMutation.mutate(submitData);
  };

  const handleDelete = (accountId: number) => {
    if (window.confirm('Are you sure you want to delete this customer debt account?')) {
      deleteMutation.mutate(accountId);
    }
  };

  const isLoading = sitesLoading || accountsLoading;

  return (
    <div className={`container mx-auto px-4 py-8 ${className || ''}`}>
      <h1 className="section-title">CustomerDebtAccounts</h1>
      
      {/* Loading Alert */}
      {isLoading && (
        <div className="alert alert-info">
          <span>Loading...</span>
        </div>
      )}

      {/* Success/Error Messages */}
      {message && (
        <div className={`alert ${message.type === 'success' ? 'alert-success' : message.type === 'error' ? 'alert-danger' : 'alert-info'}`}>
          <span>{message.text}</span>
        </div>
      )}

      {/* Site Selection */}
      <div className="mb-6">
        <label className="form-label">Site:</label>
        <select
          value={selectedSiteId || ''}
          onChange={(e) => handleSiteChange(parseInt(e.target.value))}
          className="form-input"
          disabled={isLoading}
        >
          {sites.map((site: Site) => (
            <option key={site.id} value={site.id}>
              {site.url}
            </option>
          ))}
        </select>
      </div>

      {/* Customer Debt Accounts Table */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="table table-striped">
            <thead>
              <tr>
                <th>ID</th>
                <th>Site ID</th>
                <th>Currency code</th>
                <th>Account</th>
                <th>Payment Account</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {customerDebtAccounts.map((account: FortnoxCustomerDebtAccountType) => (
                <tr key={account.id}>
                  <td>{account.id}</td>
                  <td>{account.site_id}</td>
                  <td>{account.currency_code}</td>
                  <td>{account.account}</td>
                  <td>{account.payment_account}</td>
                  <td className="text-right w-24">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => handleEditAccount(account)}
                        className="btn-icon btn-icon-secondary"
                        title="Edit"
                        disabled={isLoading}
                      >
                        <FontAwesomeIcon icon={faEdit} className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(account.id!)}
                        className="btn-icon btn-icon-danger"
                        title="Delete"
                        disabled={isLoading || deleteMutation.isLoading}
                      >
                        {deleteMutation.isLoading ? (
                          <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                        ) : (
                          <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Button */}
      <div className="mt-6">
        <button
          onClick={handleOpenModal}
          className="px-4 py-2 text-white rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50"
          style={{ backgroundColor: 'hsl(15 100% 52%)' }}
          disabled={isLoading}
        >
          Add Customer Debt Account
        </button>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title">
                {editingAccount?.id ? `Edit customer debt account ID#${editingAccount.id}` : 'New customer debt account'}
              </h2>
              <button
                onClick={handleCloseModal}
                className="modal-close"
                disabled={saveMutation.isLoading}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="form-label">Site ID:</label>
                  <select
                    value={formData.site_id || selectedSiteId || ''}
                    onChange={(e) => handleInputChange('site_id', parseInt(e.target.value))}
                    className="form-input"
                    disabled={isLoading}
                  >
                    {sites.map((site: Site) => (
                      <option key={site.id} value={site.id}>
                        {site.url}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="form-label">Currency code:</label>
                  <select
                    value={formData.currency_code || ''}
                    onChange={(e) => handleInputChange('currency_code', e.target.value)}
                    className="form-input"
                    disabled={isLoading}
                    required
                  >
                    <option value="">Select Currency</option>
                    <option value="USD">USD - US Dollar</option>
                    <option value="EUR">EUR - Euro</option>
                    <option value="GBP">GBP - British Pound</option>
                    <option value="JPY">JPY - Japanese Yen</option>
                    <option value="AUD">AUD - Australian Dollar</option>
                    <option value="CAD">CAD - Canadian Dollar</option>
                    <option value="CHF">CHF - Swiss Franc</option>
                    <option value="CNY">CNY - Chinese Yuan</option>
                    <option value="SEK">SEK - Swedish Krona</option>
                    <option value="NZD">NZD - New Zealand Dollar</option>
                    <option value="MXN">MXN - Mexican Peso</option>
                    <option value="SGD">SGD - Singapore Dollar</option>
                    <option value="HKD">HKD - Hong Kong Dollar</option>
                    <option value="NOK">NOK - Norwegian Krone</option>
                    <option value="TRY">TRY - Turkish Lira</option>
                    <option value="RUB">RUB - Russian Ruble</option>
                    <option value="INR">INR - Indian Rupee</option>
                    <option value="BRL">BRL - Brazilian Real</option>
                    <option value="ZAR">ZAR - South African Rand</option>
                    <option value="DKK">DKK - Danish Krone</option>
                    <option value="PLN">PLN - Polish Zloty</option>
                    <option value="TWD">TWD - Taiwan Dollar</option>
                    <option value="THB">THB - Thai Baht</option>
                    <option value="MYR">MYR - Malaysian Ringgit</option>
                    <option value="PHP">PHP - Philippine Peso</option>
                    <option value="IDR">IDR - Indonesian Rupiah</option>
                    <option value="KRW">KRW - South Korean Won</option>
                    <option value="VND">VND - Vietnamese Dong</option>
                    <option value="AED">AED - UAE Dirham</option>
                    <option value="SAR">SAR - Saudi Riyal</option>
                    <option value="QAR">QAR - Qatari Riyal</option>
                    <option value="KWD">KWD - Kuwaiti Dinar</option>
                    <option value="BHD">BHD - Bahraini Dinar</option>
                    <option value="OMR">OMR - Omani Rial</option>
                    <option value="JOD">JOD - Jordanian Dinar</option>
                    <option value="LBP">LBP - Lebanese Pound</option>
                    <option value="EGP">EGP - Egyptian Pound</option>
                    <option value="MAD">MAD - Moroccan Dirham</option>
                    <option value="TND">TND - Tunisian Dinar</option>
                    <option value="DZD">DZD - Algerian Dinar</option>
                    <option value="LYD">LYD - Libyan Dinar</option>
                    <option value="SDG">SDG - Sudanese Pound</option>
                    <option value="ETB">ETB - Ethiopian Birr</option>
                    <option value="KES">KES - Kenyan Shilling</option>
                    <option value="UGX">UGX - Ugandan Shilling</option>
                    <option value="TZS">TZS - Tanzanian Shilling</option>
                    <option value="NGN">NGN - Nigerian Naira</option>
                    <option value="GHS">GHS - Ghanaian Cedi</option>
                    <option value="XOF">XOF - West African CFA Franc</option>
                    <option value="XAF">XAF - Central African CFA Franc</option>
                    <option value="CDF">CDF - Congolese Franc</option>
                    <option value="AOA">AOA - Angolan Kwanza</option>
                    <option value="MZN">MZN - Mozambican Metical</option>
                    <option value="BWP">BWP - Botswanan Pula</option>
                    <option value="SZL">SZL - Swazi Lilangeni</option>
                    <option value="LSL">LSL - Lesotho Loti</option>
                    <option value="NAD">NAD - Namibian Dollar</option>
                    <option value="ZMW">ZMW - Zambian Kwacha</option>
                    <option value="MWK">MWK - Malawian Kwacha</option>
                    <option value="BIF">BIF - Burundian Franc</option>
                    <option value="RWF">RWF - Rwandan Franc</option>
                    <option value="DJF">DJF - Djiboutian Franc</option>
                    <option value="SOS">SOS - Somali Shilling</option>
                    <option value="ERN">ERN - Eritrean Nakfa</option>
                    <option value="SSP">SSP - South Sudanese Pound</option>
                    <option value="CVE">CVE - Cape Verdean Escudo</option>
                    <option value="STN">STN - São Tomé and Príncipe Dobra</option>
                    <option value="GMD">GMD - Gambian Dalasi</option>
                    <option value="GNF">GNF - Guinean Franc</option>
                    <option value="SLL">SLL - Sierra Leonean Leone</option>
                    <option value="LRD">LRD - Liberian Dollar</option>
                    <option value="XPF">XPF - CFP Franc</option>
                    <option value="TOP">TOP - Tongan Pa'anga</option>
                    <option value="WST">WST - Samoan Tala</option>
                    <option value="VUV">VUV - Vanuatu Vatu</option>
                    <option value="SBD">SBD - Solomon Islands Dollar</option>
                    <option value="PGK">PGK - Papua New Guinean Kina</option>
                    <option value="FJD">FJD - Fijian Dollar</option>
                    <option value="NPR">NPR - Nepalese Rupee</option>
                    <option value="BTN">BTN - Bhutanese Ngultrum</option>
                    <option value="MVR">MVR - Maldivian Rufiyaa</option>
                    <option value="LKR">LKR - Sri Lankan Rupee</option>
                    <option value="AFN">AFN - Afghan Afghani</option>
                    <option value="PKR">PKR - Pakistani Rupee</option>
                    <option value="BDT">BDT - Bangladeshi Taka</option>
                    <option value="MMK">MMK - Myanmar Kyat</option>
                    <option value="LAK">LAK - Lao Kip</option>
                    <option value="KHR">KHR - Cambodian Riel</option>
                    <option value="BND">BND - Brunei Dollar</option>
                    <option value="ISK">ISK - Icelandic Krona</option>
                    <option value="CZK">CZK - Czech Koruna</option>
                    <option value="HUF">HUF - Hungarian Forint</option>
                    <option value="RON">RON - Romanian Leu</option>
                    <option value="BGN">BGN - Bulgarian Lev</option>
                    <option value="HRK">HRK - Croatian Kuna</option>
                    <option value="RSD">RSD - Serbian Dinar</option>
                    <option value="MKD">MKD - Macedonian Denar</option>
                    <option value="ALL">ALL - Albanian Lek</option>
                    <option value="BAM">BAM - Bosnia-Herzegovina Convertible Mark</option>
                    <option value="MDL">MDL - Moldovan Leu</option>
                    <option value="UAH">UAH - Ukrainian Hryvnia</option>
                    <option value="BYN">BYN - Belarusian Ruble</option>
                    <option value="GEL">GEL - Georgian Lari</option>
                    <option value="AMD">AMD - Armenian Dram</option>
                    <option value="AZN">AZN - Azerbaijani Manat</option>
                    <option value="KZT">KZT - Kazakhstani Tenge</option>
                    <option value="KGS">KGS - Kyrgystani Som</option>
                    <option value="TJS">TJS - Tajikistani Somoni</option>
                    <option value="TMT">TMT - Turkmenistani Manat</option>
                    <option value="UZS">UZS - Uzbekistani Som</option>
                    <option value="MNT">MNT - Mongolian Tugrik</option>
                    <option value="ILS">ILS - Israeli New Shekel</option>
                    <option value="PEN">PEN - Peruvian Sol</option>
                    <option value="CLP">CLP - Chilean Peso</option>
                    <option value="COP">COP - Colombian Peso</option>
                    <option value="ARS">ARS - Argentine Peso</option>
                    <option value="UYU">UYU - Uruguayan Peso</option>
                    <option value="PYG">PYG - Paraguayan Guarani</option>
                    <option value="BOB">BOB - Bolivian Boliviano</option>
                    <option value="VES">VES - Venezuelan Bolívar</option>
                    <option value="GYD">GYD - Guyanese Dollar</option>
                    <option value="SRD">SRD - Surinamese Dollar</option>
                    <option value="TTD">TTD - Trinidad and Tobago Dollar</option>
                    <option value="BBD">BBD - Barbadian Dollar</option>
                    <option value="JMD">JMD - Jamaican Dollar</option>
                    <option value="BSD">BSD - Bahamian Dollar</option>
                    <option value="BZD">BZD - Belize Dollar</option>
                    <option value="GTQ">GTQ - Guatemalan Quetzal</option>
                    <option value="HNL">HNL - Honduran Lempira</option>
                    <option value="NIO">NIO - Nicaraguan Córdoba</option>
                    <option value="CRC">CRC - Costa Rican Colón</option>
                    <option value="PAB">PAB - Panamanian Balboa</option>
                    <option value="DOP">DOP - Dominican Peso</option>
                    <option value="HTG">HTG - Haitian Gourde</option>
                    <option value="CUP">CUP - Cuban Peso</option>
                    <option value="XCD">XCD - East Caribbean Dollar</option>
                    <option value="AWG">AWG - Aruban Florin</option>
                    <option value="ANG">ANG - Netherlands Antillean Guilder</option>
                    <option value="KYD">KYD - Cayman Islands Dollar</option>
                    <option value="BMD">BMD - Bermudian Dollar</option>
                    <option value="FKP">FKP - Falkland Islands Pound</option>
                    <option value="GIP">GIP - Gibraltar Pound</option>
                    <option value="SHP">SHP - Saint Helena Pound</option>
                    <option value="IMP">IMP - Manx Pound</option>
                    <option value="JEP">JEP - Jersey Pound</option>
                    <option value="GGP">GGP - Guernsey Pound</option>
                  </select>
                </div>

                <div>
                  <label className="form-label">Account:</label>
                  <input
                    type="number"
                    value={formData.account || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Only allow 4-digit numbers
                      if (value === '' || (value.length <= 4 && /^\d+$/.test(value))) {
                        handleInputChange('account', value === '' ? '' : parseInt(value));
                      }
                    }}
                    className="form-input"
                    disabled={isLoading}
                    required
                    min="1000"
                    max="9999"
                    placeholder="4 digits"
                  />
                </div>

                <div>
                  <label className="form-label">Payment Account:</label>
                  <input
                    type="number"
                    value={formData.payment_account || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Only allow 4-digit numbers
                      if (value === '' || (value.length <= 4 && /^\d+$/.test(value))) {
                        handleInputChange('payment_account', value === '' ? '' : parseInt(value));
                      }
                    }}
                    className="form-input"
                    disabled={isLoading}
                    required
                    min="1000"
                    max="9999"
                    placeholder="4 digits"
                  />
                </div>

                <div className="flex justify-end pt-6 space-x-3">
                  <button
                    type="submit"
                    className="px-4 py-2 text-white rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50"
          style={{ backgroundColor: 'hsl(15 100% 52%)' }}
                    disabled={isLoading || saveMutation.isLoading}
                  >
                    {saveMutation.isLoading ? 'Saving...' : 'Save customer debt account'}
                  </button>
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="btn btn-secondary"
                    disabled={saveMutation.isLoading}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FortnoxCustomerDebtAccounts;

